import {
  Dispatch,
  SetStateAction,
  useCallback,
  useState,
  useRef,
  useEffect,
} from "react";
import { useAsyncResult } from "../../Hooks/UseAsyncResult";
import { ApprovalLinkData, PipelineRun } from "../../Types";
import { fetchExtractLinks } from "../../Api/ado.api";

export function useFetchApprovalLinks(
  pipelineRuns: PipelineRun[],
  setError: Dispatch<SetStateAction<any>>,
) {
  const [approvalData, setApprovalLinks] = useState<
    Record<number, ApprovalLinkData | null>
  >({});
  const [loadingApprovals, setLoadingApprovals] = useState<
    Record<number, boolean>
  >({});
  const [ev2PortalLinks, setEv2PortalLinks] = useState<
    Record<number, string | null>
  >({});
  const [loadingEv2Links, setLoadingEv2Links] = useState<
    Record<number, boolean>
  >({});

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const func = useCallback(
    async (_disposable, _force) => {
      if (pipelineRuns.length === 0) {
        return;
      }
      const runsNeedingLinks = pipelineRuns
        .filter((run: PipelineRun) => {
          const needsApproval =
            approvalData[run.id] === undefined && !loadingApprovals[run.id];
          const needsEv2 =
            ev2PortalLinks[run.id] === undefined && !loadingEv2Links[run.id];
          return needsApproval || needsEv2;
        })
        .map((run: PipelineRun) => ({
          runId: run.id,
          pipelineId: run.pipeline.id,
        }));
      runsNeedingLinks.forEach(({ runId }) => {
        if (
          approvalData[runId] !== undefined &&
          ev2PortalLinks[runId] !== undefined
        ) {
          return;
        }

        if (!approvalData[runId]) {
          setLoadingApprovals((prev) => ({ ...prev, [runId]: true }));
        }
        if (!ev2PortalLinks[runId]) {
          setLoadingEv2Links((prev) => ({ ...prev, [runId]: true }));
        }

        fetchExtractLinks(runId)
          .then(({ approvalLink, ev2PortalLink }) => {
            // Only update state if component is still mounted
            if (isMountedRef.current) {
              setApprovalLinks((prev) => ({
                ...prev,
                [runId]: approvalLink,
              }));
              setEv2PortalLinks((prev) => ({
                ...prev,
                [runId]: ev2PortalLink,
              }));
            }
          })
          .catch(() => {
            // Only update state if component is still mounted
            if (isMountedRef.current) {
              setApprovalLinks((prev) => ({
                ...prev,
                [runId]: { approvalLink: null, status: null },
              }));
              setEv2PortalLinks((prev) => ({ ...prev, [runId]: null }));
            }
          })
          .finally(() => {
            // Only update state if component is still mounted
            if (isMountedRef.current) {
              setLoadingApprovals((prev) => ({ ...prev, [runId]: false }));
              setLoadingEv2Links((prev) => ({ ...prev, [runId]: false }));
            }
          });
      });
    },
    [
      approvalData,
      ev2PortalLinks,
      loadingApprovals,
      loadingEv2Links,
      pipelineRuns,
    ],
  );

  const asyncResult = useAsyncResult({
    func,
    setError,
  });

  return {
    ...asyncResult,
    approvalData,
    ev2PortalLinks,
    loadingApprovals,
    loadingEv2Links,
  };
}
